import React, { useEffect, useState } from 'react';
import { Loader2, FileText, Check } from 'lucide-react';
import { getAgentWorkspaceTree } from '@stepflow/ui-shared/services/toolsApi';
import { shareDoOutput } from '@stepflow/ui-shared/services/dataPlaneApi';

/** The agent's own direct-lane area (mirrors ``direct_run.work_area``). Pure. */
export function workArea(agentId) {
  const safe = String(agentId || '').trim().replace(/[^A-Za-z0-9_.-]/g, '_') || 'agent';
  return `context/${safe}/`;
}

/**
 * THE WORK CARD's outputs (CONTEXT-DOCS.md §H1) — what a Do run left in its agent's area, each with the next step
 * made EXPLICIT: Open (the Doc pane) · Add to context (read → digested into the next build, labelled as the
 * agent's work) · Use in Plan (into the plan pack for the hand-off). Nothing moves on its own.
 */
export default function WorkOutputs({ networkId, poolId, run, workspace, onOpen, planPicks = [], canShare = true }) {
  const [state, setState] = useState({ loading: true, files: [], error: '' });
  const [done, setDone] = useState({});         // path → 'context' | 'plan' (this session)
  const [busy, setBusy] = useState('');
  const area = workArea(run.agentId);
  const wsId = workspace && workspace.workspaceId;
  useEffect(() => {
    let gone = false;
    if (!networkId || !wsId) { setState({ loading: false, files: [], error: '' }); return undefined; }
    setState({ loading: true, files: [], error: '' });
    Promise.resolve()
      .then(() => getAgentWorkspaceTree(networkId, '_room_', area, { workspaceId: wsId }))
      .then((t) => {
        if (gone) return;
        const files = ((t && t.entries) || []).filter((e) => e && !e.isDir);
        setState({ loading: false, files, error: '' });
      })
      .catch((e) => { if (!gone) setState({ loading: false, files: [], error: (e && e.message) || "Couldn't list the files." }); });
    return () => { gone = true; };
  }, [networkId, wsId, area, run.runId, run.status]);
  const share = async (f, target) => {
    setBusy(`${f.path}|${target}`);
    try {
      await shareDoOutput(networkId, { agentId: run.agentId, path: f.path, workspaceId: wsId,
        workspaceScope: workspace.scope, target }, { pool: poolId });
      setDone((d) => ({ ...d, [`${f.path}|${target}`]: true }));
    } catch (e) {
      setState((s) => ({ ...s, error: (e && e.message) || "Couldn't do that — try again." }));
    }
    setBusy('');
  };
  if (state.loading) {
    return <div className="mt-2 flex items-center gap-1.5 text-[0.75rem] text-gray-400"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Looking at what it made…</div>;
  }
  if (!state.files.length) {
    return state.error
      ? <div className="mt-2 text-[0.75rem] text-rose-600" data-testid={`work-outputs-error-${run.runId}`}>{state.error}</div>
      : <div className="mt-2 text-[0.75rem] text-gray-400" data-testid={`work-outputs-empty-${run.runId}`}>No files in {area} yet.</div>;
  }
  const picked = new Set((planPicks || []).map((p) => p.name));
  const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
  return (
    <div className="mt-2 border-t border-gray-100 pt-2" data-testid={`work-outputs-${run.runId}`}>
      <div className="mb-1 text-[0.75rem] text-gray-500">In {run.agentName || run.agentId}&apos;s area <code className="text-[0.6875rem]">{area}</code></div>
      <ul className="space-y-1">
        {state.files.map((f) => {
          const inCtx = done[`${f.path}|context`];
          const inPlan = done[`${f.path}|plan`] || picked.has(f.name);
          return (
            <li key={f.path} className="flex items-center gap-2" data-testid={`work-output-${f.name}`}>
              <FileText className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
              <button type="button" onClick={() => onOpen(f)} className="min-w-0 flex-1 truncate text-left text-[0.8125rem] font-medium text-indigo-700 hover:underline"
                title={`Open ${f.name}`} data-testid={`work-output-open-${f.name}`}>{f.name}</button>
              {canShare ? (<>
                <button type="button" disabled={!!busy || inCtx} onClick={() => share(f, 'context')} data-testid={`work-output-context-${f.name}`}
                  title="Read it into the next context build (labelled as the agent's work)"
                  className={`${btn} ${inCtx ? 'text-emerald-700' : 'text-gray-700 hover:bg-gray-100'}`}>
                  {inCtx ? <><Check className="mr-0.5 inline h-3 w-3" aria-hidden />In context</> : 'Add to context'}
                </button>
                <button type="button" disabled={!!busy || inPlan} onClick={() => share(f, 'plan')} data-testid={`work-output-plan-${f.name}`}
                  title="Include it in what you hand to Plan"
                  className={`${btn} ${inPlan ? 'text-emerald-700' : 'text-gray-700 hover:bg-gray-100'}`}>
                  {inPlan ? <><Check className="mr-0.5 inline h-3 w-3" aria-hidden />In plan pack</> : 'Use in Plan'}
                </button>
              </>) : null}
            </li>
          );
        })}
      </ul>
      {state.error ? <div className="mt-1 text-[0.75rem] text-rose-600">{state.error}</div> : null}
    </div>
  );
}
